import * as React from "react";
import { translate, getLang } from "@/modules/shared/IntlProviderWrapper";

import { getBackend, IBackend } from "./backend/backend2";
import * as man_t from "./backend/manufacturing2.generated.types";
import * as tracking_t from "./backend/tracking.generated.types";
import * as t2 from "./simulationReportModels";
import * as kg from "./backend/kg.generated.api";
import { getUserPlants, getUserLineGroups, getLoggedinUser, getOrgData } from "./auth";
import {
    shiftNumber,
    fromShiftTag,
    toISOTimeString,
    toISODateString,
    niceNumber,
    positiveModulo,
    TIME_RANGES,
    shiftTag,
    SHIFT_CONSTS,
    removeMarkdownFromText,
} from "./util";
import { JOB_STATUS } from "./CommonConsts.generated";
import {
    COPY_WEEKLY_ASSIGNMENTS_STRATEGY,
    INSIGHT_TYPES,
    LINE_TAGS_ACCESS,
    PLANT_TAGS_ACCESS,
} from "./ManufacturingTags.generated";
import { subscribe, MSG_TYPES, publish } from "./pubSub";
import * as Auth from "./auth";
import { KG_RELATIONS } from "./models";
import local_settings, {
    FIELD_FEATURE_MATRIX_SET,
    FIELD_FEATURES_MANUFACTURING,
    FIELD_FEATURES_BASIC,
    FIELD_SYS_FLAG_ENV,
    FIELD_SYS_FLAG_LANG,
    FIELD_SYS_FLAG_INTEGRATION,
    FIELD_LAST_PLANT,
} from "./localSettings";
import SimpleMarkdownRenderer from "../modules/shared/ui/SimpleMarkdownRenderer";

//  Should be called on app start (initial page load)
export function clearFeatureMatrix() {
    local_settings.setItem(FIELD_FEATURE_MATRIX_SET, "false");
}

export function getInsightDowntimeTitle({ extra_data }: { extra_data: man_t.IEventDataEx["extra_data"] }): string {
    const user_lang = getLang();
    if (extra_data.title_enriched_lang && extra_data.title_enriched_lang[user_lang]) {
        return extra_data.title_lang[user_lang];
    }
    return extra_data.title;
}

export function getInsightTooltipTextDowntime(insight: man_t.IEventDataEx): string {
    const d = toISOTimeString(new Date(insight.ts));
    const dn = insight.extra_data.duration || (insight.ts - insight.ts_to) / TIME_RANGES.HOUR;
    const n = niceNumber(dn, 1);
    const comment = insight.extra_data.user_comment ? "- " + insight.extra_data.user_comment : "";
    const user_lang = getLang();
    let title = insight.extra_data.title;
    if (insight.extra_data.title_enriched_lang && insight.extra_data.title_enriched_lang[user_lang]) {
        title = insight.extra_data.title_enriched_lang[user_lang];
    }
    return `${title} @${d} - ${n}h ${comment}`;
}

export function getInsightTooltipTextToolSetup(insight: man_t.IEventDataEx): string {
    return `${translate("common.tool_setup", "Tool setup")}: ${insight.extra_data.material_title} (${insight.extra_data.material})`;
}

export function getInsightTooltipTextToolOverlap(insight: man_t.IEventDataEx): string {
    return `${insight.title}\n${insight.description}`;
}

export function getInsightTooltipTextNoWorkInShift(insight: man_t.IEventDataEx): string {
    return `${insight.title}: ${insight.description}`;
}

export function getInsightTooltipTextOperationDelayDownstream(insight: man_t.IEventDataEx): string {
    return `${insight.title}\n${insight.description}`;
}

export function getInsightTooltipTextOperationNoInputMaterial(insight: man_t.IEventDataEx): string {
    return `${insight.title}\n${insight.description}`;
}

export function getInsightTooltipTextOperationConstraintViolated(insight: man_t.IEventDataEx): string {
    return `${insight.title}. ${insight.description}`;
}

export function getInsightTooltipTextOutOfStock(insight: man_t.IEventDataEx): string {
    return `${insight.title}\n${insight.description}`;
}

export function getConditionalCapacityViolated(insight: man_t.IEventDataEx): string {
    return `${insight.title}\n${insight.description}`;
}

export function getPlannedDateDiff(insight: man_t.IEventDataEx): string {
    return `${insight.title}\n${insight.description}`;
}

export function getInsightTooltipText(insight: man_t.IEventDataEx): string {
    if (insight.type === INSIGHT_TYPES.man_downtime) {
        return getInsightTooltipTextDowntime(insight);
    }
    if (insight.type === INSIGHT_TYPES.tool_setup) {
        return getInsightTooltipTextToolSetup(insight);
    }
    if (insight.type === INSIGHT_TYPES.tool_overlap) {
        return getInsightTooltipTextToolOverlap(insight);
    }
    if (insight.type === INSIGHT_TYPES.no_work_in_shift) {
        return getInsightTooltipTextNoWorkInShift(insight);
    }
    if (insight.type === INSIGHT_TYPES.operation_delay_downstream) {
        return getInsightTooltipTextOperationDelayDownstream(insight);
    }
    if (insight.type === INSIGHT_TYPES.operation_no_input_material) {
        return getInsightTooltipTextOperationNoInputMaterial(insight);
    }
    if (insight.type === INSIGHT_TYPES.operation_constraint_violated) {
        return getInsightTooltipTextOperationConstraintViolated(insight);
    }
    if (insight.type === INSIGHT_TYPES.out_of_stock_plant) {
        return getInsightTooltipTextOutOfStock(insight);
    }
    if (insight.type === INSIGHT_TYPES.conditional_capacity_violated) {
        return getConditionalCapacityViolated(insight);
    }
    if (insight.type === INSIGHT_TYPES.planned_date_diff) {
        return getPlannedDateDiff(insight);
    }
    return "";
}

export function isProdEnv(): boolean {
    switch (getSysFlag("env")) {
        case "dev":
            return false;
        case "beta":
            return false;
        case "alpha":
            return false;
        default:
            return true;
    }
}

export function isDevEnv(): boolean {
    return getSysFlag("env") === "dev";
}

export function isQlectorUserOnProd(): boolean {
    const is_prod = isProdEnv();
    const user = getLoggedinUser();
    return is_prod && user.username.endsWith("qlector.com");
}

export function getInsightTooltipContent(insight: man_t.IEventDataEx): React.ReactNode {
    const insight_title = removeMarkdownFromText(insight.title);
    const insight_description = removeMarkdownFromText(insight.description);

    switch (insight.type) {
        case INSIGHT_TYPES.man_downtime: {
            const d = toISOTimeString(new Date(insight.ts));
            const n = niceNumber(insight.extra_data.duration, 1);
            const comment = insight.extra_data.user_comment ? "- " + insight.extra_data.user_comment : "";
            const user_lang = getLang();
            let title = insight.extra_data.title;
            if (insight.extra_data.title_enriched_lang && insight.extra_data.title_enriched_lang[user_lang]) {
                title = insight.extra_data.title_enriched_lang[user_lang];
            }
            return (
                <>
                    <strong>{title}</strong> {d} - {n}h {comment}
                </>
            );
        }
        case INSIGHT_TYPES.tool_setup:
            return (
                <>
                    <strong>{translate("common.tool_setup", "Tool setup")}</strong>: {insight.extra_data.material_title}{" "}
                    ({insight.extra_data.material})
                </>
            );
        case INSIGHT_TYPES.tool_overlap:
        case INSIGHT_TYPES.operation_delay_downstream:
        case INSIGHT_TYPES.operation_no_input_material:
        case INSIGHT_TYPES.out_of_stock_plant:
            return (
                <>
                    <SimpleMarkdownRenderer markdown={insight.title} />
                    <SimpleMarkdownRenderer markdown={insight.description} />
                </>
            );
        case INSIGHT_TYPES.no_work_in_shift:
            return (
                <>
                    <strong>{insight_title}</strong>: {insight_description}
                </>
            );
        case INSIGHT_TYPES.operation_constraint_violated:
            return (
                <>
                    <strong>{insight_title}</strong>. {insight_description}
                </>
            );
        case INSIGHT_TYPES.conditional_capacity_violated:
            return (
                <>
                    <strong>{insight_title}</strong>. {insight_description}
                </>
            );
        case INSIGHT_TYPES.planned_date_diff:
            return (
                <>
                    <strong>{insight_title}</strong>. {insight_description}
                </>
            );
        default:
            return null;
    }
}

export function getOrderModelParamsTooltip(order: t2.ISimulationReportOrderEx): string {
    if (order.model_normative_fallback) {
        const base = translate("ShiftTableProduction.based_on_normative", "Prediction based on normative");
        return order.model_name ? `${base}\n${order.model_name}` : base;
    } else {
        return (
            translate("ShiftTableProduction.based_on_history", "Prediction based on history") +
            "\n" +
            `${translate("ShiftTableProduction.hours_remaining", "hoursRemaining")} ` +
            `= ${niceNumber(order.model_slope, 4)} ${translate("ShiftTableProduction.parts_remaining", "partsRemaining")} ` +
            `+ (${translate("ShiftTableProduction.parts_remaining", "partsRemaining")} / ` +
            `${translate("ShiftTableProduction.parts_total", "partsTotal")}) x ${niceNumber(order.model_intercept, 4)}`
        );
    }
}

export async function initBusinessLogic() {
    try {
        await loadFeatureMatrixAsync();
    } finally {
        await initManufacturingCache();
    }
}

export async function loadFeatureMatrixAsync() {
    if (
        local_settings.getItem(FIELD_FEATURE_MATRIX_SET) === null ||
        local_settings.getItem(FIELD_FEATURE_MATRIX_SET) === "false"
    ) {
        const response = await getBackend().common.getFeaturesMatrix({});
        local_settings.setItem(FIELD_FEATURE_MATRIX_SET, "true");
        local_settings.setItem(FIELD_FEATURES_BASIC, response.basic ? "true" : "false");
        local_settings.setItem(FIELD_FEATURES_MANUFACTURING, response.manufacturing ? "true" : "false");
        const sys_flags = response.sys_flags;
        local_settings.setItem(FIELD_SYS_FLAG_ENV, (sys_flags.env || "dev").toLowerCase());
        local_settings.setItem(FIELD_SYS_FLAG_LANG, sys_flags.lang || "en");
        local_settings.setItem(FIELD_SYS_FLAG_INTEGRATION, sys_flags.integration || "");
    }
}

export function displayFeature(feature: string): boolean {
    if (
        local_settings.getItem(FIELD_FEATURE_MATRIX_SET) === null ||
        local_settings.getItem(FIELD_FEATURE_MATRIX_SET) === "false"
    ) {
        console.log("displayFeature() - Features matrix not initialized yet!");
        return false;
    }
    if (feature === "manufacturing") {
        return local_settings.getItem(FIELD_FEATURES_MANUFACTURING) === "true";
    }
    if (feature === "basic") {
        return true;
    }
    return false;
}

export function getSysFlag(sysFlag: string): string {
    if (
        local_settings.getItem(FIELD_FEATURE_MATRIX_SET) === null ||
        local_settings.getItem(FIELD_FEATURE_MATRIX_SET) === "false"
    ) {
        console.log("getSysFlag() - Features matrix not initialized yet!");
        return "";
    }
    if (sysFlag === "env") {
        return local_settings.getItem(FIELD_SYS_FLAG_ENV) || "";
    }
    if (sysFlag === "lang") {
        return local_settings.getItem(FIELD_SYS_FLAG_LANG) || "";
    }
    if (sysFlag === "integration") {
        return local_settings.getItem(FIELD_SYS_FLAG_INTEGRATION) || "";
    }
    return "";
}

/** Detect situation when insight's ts belongs to different shift than its content. Change ts to appropriate value */
export function updateTsForInsights(insights: man_t.IEventDataEx[]): void {
    insights.forEach(insight => {
        const shiftTag1 = shiftNumber(new Date(insight.ts)).shift_tag;
        if (insight.tags.shift_tag && insight.tags.shift_ts && shiftTag1 !== insight.tags.shift_tag) {
            // tag indicates that this insight "belongs" to another shift - change ts to achieve correct ordering
            insight.ts = +insight.tags.shift_ts;
        }
    });
}

/** Override timestamps for insights with "start-of-shift" timestamps */
export function updateTsForInsightsToShift(insights: man_t.IEventDataEx[]): void {
    insights.forEach(insight => {
        if (insight.tags.shift_tag) {
            const d = fromShiftTag(insight.tags.shift_tag);
            insight.extra_data.$ts = d.getTime();
        } else {
            insight.extra_data.$ts = insight.ts;
        }
    });
}

/** Add currently active statuses to simulation report orders */
export async function addCurrentlyActiveStatuses(report: t2.IReportModelEx, lines: string[]): Promise<void> {
    const response: man_t.IGetLineStatusesRes = await getBackend().manufacturing.getLineStatuses({
        lineUuids: lines,
        includeStale: true,
    });

    for (const line of report.result.orders) {
        const line_status = response.data.find(x => x.lineUuid == line.line);
        if (line_status === undefined) {
            continue;
        }

        const current_order = line_status.live?.currentOrder ?? line_status.inferredProduction?.currentOrder;
        if (current_order === undefined) {
            continue;
        }

        // Live currentOrder uses workOrderId; inferred uses orderExternalId (orderUuid is required only on inferred).
        const orderExternalId =
            "orderUuid" in current_order ? current_order.orderExternalId : current_order.workOrderId;

        for (const order of line.production) {
            if (
                orderExternalId != null &&
                order.order_external_id === orderExternalId &&
                order.process_num === current_order.processNum
            ) {
                order.is_currently_active = true;
                break;
            }
        }
    }
}

//////////////////////////////////////////////////////////////////////////////////////////////////

// array of plant objects
let cache_plants: man_t.IPlantData[] = [];
// array of line groups
let cache_line_groups: man_t.ILineGroupData[] = [];
// map of linegroups: <linegroup_uuid, linegroup_object>
let cache_line_groups_map: Map<string, man_t.ILineGroupData> = new Map();

/** Cached line fields from manufacturing `getLines` (weight/title/type). */
type CachedLineRecord = {
    weight: number;
    title: string;
    type: string;
};

// map of line_uuid -> weight, title, type (from getLines refresh)
let cache_line_map: Map<string, CachedLineRecord> = new Map();
let pending_callbacks: (() => void)[] | null = []; // empty array indicates cache is still pending

/** Initializes manufacturing cache */
async function initManufacturingCache() {
    subscribe(MSG_TYPES.user_profile_reloaded, async () => {
        await cacheRefresh();
        const dt = new Date().toISOString();
        console.log(dt, "Automatic BL cache refresh done!");
    });
    await cacheRefresh();
}

/** Call this function before using other business logic methods */
function waitForManufacturing(done: () => void) {
    if (!pending_callbacks) {
        // array is null, cache has been initialized
        done();
        return;
    }
    pending_callbacks.push(done);
}

/** Call this Promise-based function before using other business logic methods */
export function waitForManufacturingAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
        try {
            waitForManufacturing(resolve);
        } catch (err) {
            reject(err);
        }
    });
}

/** Forces all cached data to be re-loaded, e.g. after editing. */
export async function forceManufacturingCacheRefresh(): Promise<void> {
    await cacheRefresh();
}

/** Refreshes BL cache */

async function cacheRefresh(): Promise<void> {
    if (getLoggedinUser().username === "") {
        // user not logged in - clear all caches, don't go to server
        cache_plants = [];
        cache_line_groups = [];
        cache_line_groups_map = new Map();
        cache_line_map = new Map();
        return;
    }
    const existing_data = JSON.stringify([cache_plants, cache_line_groups]);
    console.log("Refreshing BL cache...");

    // get plants
    const res_plants = await getBackend().manufacturing.getPlants({});
    const allowed_plants = getUserPlants();
    // filter only allowed plants for the user
    cache_plants = res_plants.plants;
    if (!Auth.isInElevatedRole()) {
        cache_plants = cache_plants.filter(x => allowed_plants.indexOf(x.uuid) >= 0);
    }
    // filter plants by weight
    cache_plants.sort((a, b) => (a.weight > b.weight ? 1 : -1));

    // get linegroups
    const res_linegroups = await getBackend().manufacturing.getLineGroups({});
    const allowed_line_groups = getUserLineGroups();
    // filter only allowed linegroups for the user
    cache_line_groups = res_linegroups.line_groups.filter(x => allowed_line_groups.indexOf(x.uuid) >= 0);
    // sort linegroups by weight
    cache_line_groups.sort((a, b) => (a.weight > b.weight ? 1 : -1));
    // clear
    cache_line_groups_map.clear();
    // set linegroup_uuid, linegroup_object map
    cache_line_groups.forEach(x => {
        cache_line_groups_map.set(x.uuid, x);
    });

    // get lines
    const res_lines = await getBackend().manufacturing.getLines({});
    cache_line_map.clear();
    for (const x of res_lines.lines) {
        cache_line_map.set(x.uuid, {
            weight: x.weight,
            title: x.title,
            type: LINE_TAGS_ACCESS.type(x.tags),
        });
    }

    console.log("BL cache refreshed.");
    const tmp_pending_callbacks = pending_callbacks || [];
    pending_callbacks = null;
    for (const cb of tmp_pending_callbacks) {
        cb();
    }
    const new_data = JSON.stringify([cache_plants, cache_line_groups]);
    if (new_data !== existing_data) {
        publish(MSG_TYPES.manufacturing_data_updated, {});
    } else {
        console.log("BL cache refreshed, but no changes detected.");
    }
}

// returns only line groups include plant_uuid among plants
// the user must have access to line groups and plant
export function getLineGroupsForUserPlant(plant_uuid: string): man_t.ILineGroupData[] {
    if (!cache_plants.some(x => x.uuid === plant_uuid)) {
        return [];
    } else {
        const res = cache_line_groups.filter(lg => lg.plant_uuids.length > 0 && lg.plant_uuids.includes(plant_uuid));
        return JSON.parse(JSON.stringify(res));
    }
}

// returns a single line group if the user has access to it (or null otherwise)
export function getLineGroupForUser(line_group_uuid: string): man_t.ILineGroupData | null {
    const hit = cache_line_groups_map.get(line_group_uuid);
    return hit == undefined ? null : JSON.parse(JSON.stringify(hit));
}

// returns all line groups that the user has access to
export function getLineGroupsForUser(): man_t.ILineGroupData[] {
    return JSON.parse(JSON.stringify(cache_line_groups));
}

export function getLineGroupForLine(line_uuid: string): man_t.ILineGroupData | null {
    for (const line_group of cache_line_groups) {
        for (const lu of line_group.line_uuids) {
            if (lu === line_uuid) {
                return JSON.parse(JSON.stringify(line_group));
            }
        }
    }
    return null;
}

export function getLineGroupsForLine(line_uuid: string, prefered_line_group_uuid: string): man_t.ILineGroupData[] {
    const line_groups = new Map<string, man_t.ILineGroupData>();
    for (const line_group of cache_line_groups) {
        for (const lu of line_group.line_uuids) {
            if (lu === line_uuid) {
                line_groups.set(line_group.uuid, JSON.parse(JSON.stringify(line_group)));
            }
        }
    }

    const prefered_line_group = line_groups.get(prefered_line_group_uuid);
    if (prefered_line_group) {
        return [prefered_line_group];
    }

    return [...line_groups.values()];
}

// returns all plants that the user has access to
export function getPlantsForUser(): man_t.IPlantData[] {
    return JSON.parse(JSON.stringify(cache_plants));
}

// filters an array of line objects
export function filterLinesForUser(lines: man_t.ILineData[], skip_if_admin?: boolean): man_t.ILineData[] {
    if (
        skip_if_admin &&
        (Auth.isInRole(Auth.ROLE_ADMIN) || Auth.isInRole(Auth.ROLE_POWER_USER) || Auth.isInRole(Auth.ROLE_DEMO_USER))
    ) {
        return lines;
    }
    return lines
        .filter(x => cache_line_groups.some(y => y.line_uuids.indexOf(x.uuid) >= 0))
        .filter(x => cache_plants.some(y => y.uuid === x.plant_uuid));
}

// get weight for given line
export function getLineWeight(line_uuid: string): number {
    const lineData = cache_line_map.get(line_uuid);
    if (lineData !== undefined) {
        return lineData.weight;
    }

    return -2;
}

// get title for given line
export function getLineTitle(line_uuid: string): string {
    const lineData = cache_line_map.get(line_uuid);
    if (lineData !== undefined) {
        return lineData.title;
    }

    return "";
}

/** Line type tag value (same semantics as `LINE_TAGS_ACCESS.type` on full line tags). Empty string if unknown. */
export function getLineType(line_uuid: string): string {
    const lineData = cache_line_map.get(line_uuid);
    return lineData !== undefined ? lineData.type : "";
}

export function getPlantTags(plant_uuid: string): man_t.ITags {
    const plant = getPlantsForUser().find(p => p.uuid === plant_uuid);
    return plant && plant.tags ? plant.tags : {};
}

export function getPlantTagBool(plant_uuid: string, key: string, default_val: boolean): boolean {
    const plant = getPlantsForUser().find(p => p.uuid === plant_uuid);
    return plant && plant.tags && plant.tags[key] !== undefined ? plant.tags[key] === "true" : default_val;
}

export function getPlantTagStr(plant_uuid: string, key: string, default_val: string): string {
    const plant = getPlantsForUser().find(p => p.uuid === plant_uuid);
    return plant && plant.tags && plant.tags[key] !== undefined ? plant.tags[key] : default_val;
}

type PlantTagValue = ReturnType<(typeof PLANT_TAGS_ACCESS)[keyof typeof PLANT_TAGS_ACCESS]>;
export const getPlantTagValue = (tag_id: string): PlantTagValue => {
    const plant_tags = getPlantTags(local_settings.getItem(FIELD_LAST_PLANT) ?? "");
    const tagAccessor = PLANT_TAGS_ACCESS[tag_id];
    if (typeof tagAccessor !== "function") {
        console.error(`Invalid tag key: ${tag_id}`);
        return "";
    }
    return tagAccessor(plant_tags);
};

// returns tag value if exists, else returns given default
export function getLineGroupTagStr(line_group_uuid: string, key: string, default_val: string): string {
    const line_group = getLineGroupForUser(line_group_uuid);
    return line_group !== null && line_group.tags[key] !== undefined ? line_group.tags[key] : default_val;
}

export function getLineGroupTagBool(line_group_uuid: string, key: string, default_val: boolean): boolean {
    const line_group = getLineGroupForUser(line_group_uuid);
    return line_group !== null && line_group.tags[key] !== undefined ? line_group.tags[key] === "true" : default_val;
}

// returns tag value if exists, else returns given default
export function getLineGroupTagInt(line_group_uuid: string, key: string, default_val: number): number {
    const line_group = getLineGroupForUser(line_group_uuid);
    return line_group !== null && line_group.tags[key] !== undefined ? parseInt(line_group.tags[key], 10) : default_val;
}

// returns tag value if exists, else returns given default
export function getLineGroupTagJson<T>(line_group_uuid: string, key: string, default_val: T): T {
    const line_group = getLineGroupForUser(line_group_uuid);
    return line_group !== null && line_group.tags[key] !== undefined
        ? (JSON.parse(line_group.tags[key]) as T)
        : default_val;
}

export function getLineGroupTags(line_group_uuid: string) {
    const line_group = getLineGroupForUser(line_group_uuid);
    return line_group !== null ? line_group.tags : {};
}

/** Available strategies for copying assignments between weeks */
export const ShiftCopyingStrategyFreeze = Object.freeze(COPY_WEEKLY_ASSIGNMENTS_STRATEGY);
export type ShiftCopyingStrategy = (typeof ShiftCopyingStrategyFreeze)[keyof typeof ShiftCopyingStrategyFreeze];

/** Callback for determinig allowed shift for person */
export type PersonAllowedShifts = (uuid: string) => boolean[];

/** Smart copying of assignments between weeks */
export function copyShiftAssignmentsBetweenWeeks(
    prevWeek: man_t.IShiftPersonLineRec,
    currWeek: man_t.IShiftPersonLineRec,
    strategy: ShiftCopyingStrategy,
    personAllowedShifts: PersonAllowedShifts,
    week_start_shift_offset: number,
    rotate_opposite_direction = false
) {
    // non-matching dimensions, don't do anything
    if (currWeek.shifts.length !== prevWeek.shifts.length) {
        return;
    }
    if (strategy === COPY_WEEKLY_ASSIGNMENTS_STRATEGY.copy) {
        for (let i = 0; i < currWeek.shifts.length; i++) {
            currWeek.shifts[i].persons = prevWeek.shifts[i].persons;
        }
    } else if (strategy === COPY_WEEKLY_ASSIGNMENTS_STRATEGY.shift_preference_rotate) {
        // smart rotation based on shift preference

        const psh = prevWeek.shifts;
        const csh = currWeek.shifts;
        // first reset all assignments in the new week
        for (const item of csh) {
            item.persons = [];
        }

        const shifts_per_day = 3;
        const rotate_direction = rotate_opposite_direction ? -1 : 1;

        // go through each person in and rotate her independently
        // determine the day to use as role-model - among Mon, Tue and Wed chose the one with the most assigned people
        const getAssignedPeopleForDay = (day: number) => {
            return (
                psh[shifts_per_day * day + 0].persons.length +
                psh[shifts_per_day * day + 1].persons.length +
                psh[shifts_per_day * day + 2].persons.length
            );
        };
        const people_per_day = [getAssignedPeopleForDay(0), getAssignedPeopleForDay(1), getAssignedPeopleForDay(2)];
        const highest_day = people_per_day.indexOf(Math.max(...people_per_day)); // get index of day with max value
        const anchor_shift = highest_day * shifts_per_day;
        for (let shift_of_day = anchor_shift; shift_of_day < anchor_shift + shifts_per_day; shift_of_day++) {
            for (const person of psh[shift_of_day].persons) {
                const allowed_shifts = personAllowedShifts(person.uuid);
                if (allowed_shifts.every(x => x === false)) {
                    // this person has no allowed shift, just skip her
                    continue;
                }
                // now determine the shift for this week by shifting previous-week's shift
                // and take into account allowed shifts
                let shift_this_week = positiveModulo(shift_of_day + rotate_direction, shifts_per_day);
                while (!allowed_shifts[shift_this_week]) {
                    shift_this_week += rotate_direction;
                    shift_this_week = positiveModulo(shift_this_week, shifts_per_day);
                }

                // also calculate shift for the next week (current + 1)
                let shift_next_week = positiveModulo(shift_this_week + rotate_direction, shifts_per_day);
                while (!allowed_shifts[shift_next_week]) {
                    shift_next_week += rotate_direction;
                    shift_next_week = positiveModulo(shift_next_week, shifts_per_day);
                }

                // assign this person to calculated shift in the current week
                for (let j = 0; j < 5; j++) {
                    const shift = csh[shifts_per_day * j + shift_this_week];
                    if (j === 4 && shift_this_week === 2 && week_start_shift_offset == -1) {
                        // don't add to friday night shift
                    } else {
                        shift.persons.push(person);
                    }
                }

                // if next week this person works in shift 2 (night shift), add her to this week's sunday's night shift
                if (shift_next_week === 2 && week_start_shift_offset == -1) {
                    const shift = csh[csh.length - 1];
                    shift.persons.push(person);
                }
            }
        }
    } else if (strategy === COPY_WEEKLY_ASSIGNMENTS_STRATEGY.four_shift_rotation) {
        const psh = prevWeek.shifts;
        // find the day in the week when the first shift changed from Monday's assignments
        const shift_0 = psh[0].persons.map(x => x.uuid).join(",");
        let day_of_switch = 0;
        while (++day_of_switch < 7) {
            const shift_x = psh[3 * day_of_switch].persons.map(x => x.uuid).join(",");
            if (shift_x !== shift_0) {
                break;
            }
        }
        // if we reached the day 7 this means that there is no data at all
        if (day_of_switch === 7) {
            day_of_switch = 1;
        }
        const groups: string[][] = [[], [], [], []];
        if (rotate_opposite_direction) {
            // on that day group 0 wasn't working, but the other groups were assigned like this:
            // 1. shift = group1
            // 2. shift = group2
            // 3. shift = group3
            groups[0] = psh[0].persons.map(x => x.uuid);
            groups[1] = psh[3 * day_of_switch].persons.map(x => x.uuid);
            groups[2] = psh[3 * day_of_switch + 1].persons.map(x => x.uuid);
            groups[3] = psh[3 * day_of_switch + 2].persons.map(x => x.uuid);
        } else {
            // on that day group 0 wasn't working, but the other groups were assigned like this:
            // 1. shift = group1
            // 2. shift = group3
            // 3. shift = group2
            groups[0] = psh[0].persons.map(x => x.uuid);
            groups[1] = psh[3 * day_of_switch].persons.map(x => x.uuid);
            groups[2] = psh[3 * day_of_switch + 2].persons.map(x => x.uuid);
            groups[3] = psh[3 * day_of_switch + 1].persons.map(x => x.uuid);
        }

        // collect people data
        const map_uuid2person: Map<string, man_t.IShiftPersonLineRecPerson> = new Map();
        psh[0].persons.forEach(person => map_uuid2person.set(person.uuid, person));
        psh[3 * day_of_switch].persons.forEach(person => map_uuid2person.set(person.uuid, person));
        psh[3 * day_of_switch + 1].persons.forEach(person => map_uuid2person.set(person.uuid, person));
        psh[3 * day_of_switch + 2].persons.forEach(person => map_uuid2person.set(person.uuid, person));

        // prepare template for shift-assignment-sequence
        // -1 means rest
        const shift_x: number[] = [];
        for (let ii = 0; ii <= 2; ii++) {
            let i = rotate_opposite_direction ? 2 - ii : ii;
            // 6-day work in same shift
            for (let j = 0; j < 6; j++) {
                shift_x.push(i);
            }
            // 2-day rest
            shift_x.push(-1);
            shift_x.push(-1);
        }
        const indexes: number[] = [];
        if (rotate_opposite_direction) {
            const index0 = 3 * 8 - 2 - day_of_switch + 7 + 3 * 8;
            const index1 = index0 - 6;
            const index2 = index1 - 6;
            const index3 = index2 - 6;
            indexes.push(index0 % shift_x.length);
            indexes.push(index1 % shift_x.length);
            indexes.push(index2 % shift_x.length);
            indexes.push(index3 % shift_x.length);
        } else {
            const index0 = 1 * 8 - 2 - day_of_switch + 7 + 3 * 8;
            const index1 = index0 - 6;
            const index2 = index1 - 6;
            const index3 = index2 - 6;
            indexes.push(index0 % shift_x.length);
            indexes.push(index1 % shift_x.length);
            indexes.push(index2 % shift_x.length);
            indexes.push(index3 % shift_x.length);
        }
        const csh = currWeek.shifts;
        csh.forEach(shift => {
            // reset persons for each shift
            shift.persons = [];
        });
        // loop over all days of the week
        for (let day = 0; day < 7; day++) {
            // for each group
            for (let group = 0; group < indexes.length; group++) {
                // determine the shift of the day that this group should work in on current day
                const index = indexes[group] % shift_x.length;
                const shift = shift_x[index];
                if (shift >= 0) {
                    for (const person of groups[group]) {
                        const shift_obj = csh[3 * day + shift];
                        const person_obj = map_uuid2person.get(person);
                        if (person_obj !== undefined) {
                            shift_obj.persons.push(person_obj);
                        }
                    }
                }
                indexes[group] = (indexes[group] + 1) % shift_x.length;
            }
        }
    } else {
        // COPY_WEEKLY_ASSIGNMENTS_STRATEGY.simple_rotate
        // smart rotation based on enabled shifts

        // check monday to determine the rotation type
        const psh = prevWeek.shifts;
        const csh = currWeek.shifts;
        const day_count = Math.floor(csh.length / 3);
        const sh0 = psh[0].persons.length > 0;
        const sh1 = psh[1].persons.length > 0;
        const sh2 = psh[2].persons.length > 0;
        if (sh0 && sh1 && sh2) {
            // three shift day -> permute 3 workers (shift 3 -> shift 2, shift 2 -> shift 1, shift 1 -> shift 3)
            for (let i = 0; i < day_count; i++) {
                if (rotate_opposite_direction) {
                    csh[3 * i + 0].persons = psh[3 * i + 1].persons;
                    csh[3 * i + 1].persons = psh[3 * i + 2].persons;
                    csh[3 * i + 2].persons = psh[3 * i + 0].persons;
                } else {
                    csh[3 * i + 0].persons = psh[3 * i + 2].persons;
                    csh[3 * i + 1].persons = psh[3 * i + 0].persons;
                    csh[3 * i + 2].persons = psh[3 * i + 1].persons;
                }
                if (week_start_shift_offset != 0 && i === day_count - 1 && csh[3 * i + 2].enabled) {
                    if (rotate_opposite_direction) {
                        // last shift on Sunday should be the same as the first shift of the week
                        csh[3 * i + 2].persons = csh[0].persons;
                    } else {
                        // last shift on Sunday should be the same as the second shift of the week
                        csh[3 * i + 2].persons = csh[1].persons;
                    }
                }
            }
        } else if (sh0 && sh1 && !sh2) {
            // two shift day -> switch workers
            for (let i = 0; i < day_count; i++) {
                csh[3 * i + 0].persons = psh[3 * i + 1].persons;
                csh[3 * i + 1].persons = psh[3 * i + 0].persons;
                csh[3 * i + 2].persons = psh[3 * i + 2].persons;
            }
        } else if (sh0 && !sh1 && !sh2) {
            // one shift day -> copy
            for (let i = 0; i < csh.length; i++) {
                csh[i].persons = psh[i].persons;
            }
        } else {
            // some strange thing, just copy
            for (let i = 0; i < csh.length; i++) {
                csh[i].persons = psh[i].persons;
            }
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////

export async function fullSimulation(): Promise<void> {
    /// current week
    const current_date = new Date();
    const shift_number = shiftNumber(current_date);
    const current_year = shift_number.year;
    const current_week = shift_number.week;
    const current_shift = shift_number.shift;
    const current_shift_date = toISODateString(current_date);

    // create new simulation
    await getBackend().reports.createNewReport({
        title: "Complete simulation",
        input: {
            version: 0,
            data: {
                next_shift_time: {
                    shift_date: current_shift_date,
                    shift_number: current_shift,
                    week_number: current_week,
                    year: current_year,
                },
                analysis_configuration: {
                    max_end_times_sample: 500,
                    quantiles: [0.1, 0.5, 0.9],
                    predict_shifts: 21 * 20,
                },
            },
        },
        tags: { simulation_type: "full" },
    });
}

export async function fullInsights(): Promise<void> {
    await getBackend().manufacturing.recalculateInsights({});
}

export async function calculateKpis(): Promise<void> {
    await getBackend().reports.calcKpis({});
}

export async function recomputeKnowledgeGraph(): Promise<void> {
    await getBackend().kg.refreshKnowledgeGraph({});
}

export async function getAllLinesForLineGroup(linegroup_uuid: string): Promise<man_t.IGetLinesRes> {
    const res_lines = await getBackend().manufacturing.getLines({ line_group_uuids: [linegroup_uuid] });
    return res_lines;
}

////////////////////////////////////////////////////////////////////////////////////////////////////

/** Utility method for waiting on job */
export async function waitForJob(
    backend: IBackend,
    token: string,
    autodelete = true,
    return_results = false
): Promise<unknown> {
    const CHECK_INTERVAL = 3 * TIME_RANGES.SECOND;
    const MAX_WAIT_TIME = 1000 * TIME_RANGES.MINUTE;
    return new Promise((resolve, reject) => {
        let counter = 0;
        const loopCall = async (): Promise<void> => {
            try {
                const res = await backend.common.getJobStatus({ id: token });
                if (!res) {
                    reject(new Error("Job with given uuid not found"));
                    return;
                }
                if (res.status === JOB_STATUS.errored) {
                    if (autodelete) {
                        await backend.common.deleteJob({ id: token });
                    }
                    reject(new Error("Job failed with error: " + res.status_msg));
                    return;
                }
                if (res.status === JOB_STATUS.ended) {
                    let final_result: unknown = undefined;
                    if (return_results) {
                        final_result = (await backend.common.getJobInfo({ id: token })).result;
                    }
                    if (autodelete) {
                        await backend.common.deleteJob({ id: token });
                    }
                    resolve(final_result);
                    return;
                }
                // check if we have been waiting for too long
                counter++;
                if (counter * CHECK_INTERVAL >= MAX_WAIT_TIME) {
                    reject(new Error("Job time-out: " + token));
                    return;
                }
                // make another loop
                setTimeout(loopCall, CHECK_INTERVAL);
            } catch (err) {
                reject(err);
            }
        };
        setTimeout(loopCall, CHECK_INTERVAL);
    });
}

////////////////////////////////////////////////////////////////////////////////////////////////////

export interface IMaterialKnowledgeGraph {
    consumed_by_lines: man_t.ILineData[];
    consumed_by_materials: man_t.IMaterialModel[];
    consumes_materials: man_t.IMaterialModel[];
    produced_on_lines: man_t.ILineData[];
}

export async function getMaterialKnowledgeGraph(
    kg_service: kg.IKg,
    external_id: string
): Promise<IMaterialKnowledgeGraph> {
    // execute knowledge-graph queries
    const res_cbm = await kg_service.searchSimple({
        entity2: external_id,
        relation: KG_RELATIONS.MATERIAL_CONSUMES_MATERIAL,
    });
    const consumed_by_materials = res_cbm.hits.map(x => x.entity1);

    const res_cm = await kg_service.searchSimple({
        entity1: external_id,
        relation: KG_RELATIONS.MATERIAL_CONSUMES_MATERIAL,
    });
    const consumes_materials = res_cm.hits.map(x => x.entity2);

    const res_cbl = await kg_service.searchSimple({
        entity2: external_id,
        relation: KG_RELATIONS.LINE_CONSUMES_MATERIAL,
    });
    const consumed_by_lines = res_cbl.hits.map(x => x.entity1);

    const res_pol = await kg_service.searchSimple({
        entity2: external_id,
        relation: KG_RELATIONS.LINE_PRODUCES_MATERIAL,
    });
    const produced_on_lines = res_pol.hits.map(x => x.entity1);

    let consumed_by_lines_full: man_t.ILineData[] = [];
    if (consumed_by_lines.length > 0) {
        const { lines } = await getBackend().manufacturing.getLines({ external_ids: consumed_by_lines });
        consumed_by_lines_full = lines;
    }

    let produced_on_lines_full: man_t.ILineData[] = [];
    if (produced_on_lines.length > 0) {
        const { lines } = await getBackend().manufacturing.getLines({ external_ids: produced_on_lines });
        produced_on_lines_full = lines;
    }

    let consumed_by_materials_full: man_t.IMaterialModel[] = [];
    if (consumed_by_materials.length > 0) {
        const { data } = await getBackend().manufacturing.searchMaterials({ external_ids: consumed_by_materials });
        consumed_by_materials_full = data;
    }

    let consumes_materials_full: man_t.IMaterialModel[] = [];
    if (consumes_materials.length > 0) {
        const { data } = await getBackend().manufacturing.searchMaterials({ external_ids: consumes_materials });
        consumes_materials_full = data;
    }

    return {
        consumed_by_lines: consumed_by_lines_full,
        consumed_by_materials: consumed_by_materials_full,
        consumes_materials: consumes_materials_full,
        produced_on_lines: produced_on_lines_full,
    };
}

////////////////////////////////////////////////////////////////////////////////////////////////////

export async function getCurrentOrgTags(): Promise<man_t.ITags> {
    try {
        const res = await getBackend().manufacturing.getCurrentOrganisation({});
        return res.tags;
    } catch (e) {
        console.error("Error fetching current organisation tags:", e);
        return {};
    }
}

export async function getPrivateConfig(): Promise<tracking_t.IGetPrivateConfigRes> {
    try {
        const res = await getBackend().tracking.getPrivateConfig({});
        return res;
    } catch (e) {
        console.error("Error fetching user monitoring config:", e);
        return {
            datadog_app_id: "",
            datadog_client_token: "",
            datadog_service: "",
            installation_name: "",
            version: "",
        };
    }
}

const cache_app_version = {
    leap_version: "",
    leap_version_short: "",
};

/** Loads and saves the app version to the cache */
export async function loadAndSaveAppVersion() {
    try {
        const res = await getBackend().tracking.getAppVersion({});
        cache_app_version.leap_version = res.leap_version;
        cache_app_version.leap_version_short = res.leap_version_short;
    } catch (e) {
        console.error("Error fetching app version:", e);
    }
}

export function getAppVersionShort(): string {
    return cache_app_version.leap_version_short;
}

export function getAppVersion(): string {
    return cache_app_version.leap_version;
}

/** Checks if plant offsets first shift and return starting's shift tag */
export function shiftTagOffset(year: number, week: number, shift: number, plant_uuid: string): string {
    const plants = getPlantsForUser().filter(x => x.uuid === plant_uuid);
    const plant_data = plants[0];
    const first_shift_offset = PLANT_TAGS_ACCESS.shift_board_first_shift_offset(plant_data.tags);
    const shift_tag = shiftTag(year, week, shift);

    if (first_shift_offset === 0) {
        return shift_tag;
    }

    const date_from_shift_tag = fromShiftTag(shift_tag).getTime();
    const date_from_shift_tag_with_offset = new Date(
        date_from_shift_tag + first_shift_offset * SHIFT_CONSTS.SHIFT_DURATION_HOURS * TIME_RANGES.HOUR
    );
    const shift_number = shiftNumber(date_from_shift_tag_with_offset);

    return shift_number.shift_tag;
}

export function startEndShiftTags(
    year: number,
    week: number,
    plant_uuid: string
): { start_shift_tag: string; end_shift_tag: string } {
    const start_shift_tag = shiftTagOffset(year, week, 0, plant_uuid);
    const end_shift_tag = shiftTagOffset(year, week, 21, plant_uuid);
    return { start_shift_tag, end_shift_tag };
}

const ORGS_WITH_EXTRA_COLUMNS = ["agrana"];
export const shouldAddExtraColumns = () => ORGS_WITH_EXTRA_COLUMNS.includes(getOrgData().code);
